import React from 'react';
import PropTypes from 'prop-types';
import { HiXCircle } from 'react-icons/hi';
import { BiCloudUpload } from 'react-icons/bi';
import uploadcare from 'uploadcare-widget';
import cn from 'classnames';
import { isEmpty } from 'lodash';

import IconFileJpg from 'assets/images/icons/icon-filetype-jpg.svg';
import IconFilePdf from 'assets/images/icons/icon-filetype-pdf.svg';
import IconFilePng from 'assets/images/icons/icon-filetype-png.svg';
import { showLightbox } from 'partial/components/Modal';

import DropZone from '../components/DropZone';

export const uploadFile = (options, validate) =>
  new Promise((resolve) => {
    const opt = {
      tabs: ['file', 'url', 'camera'],
      multiple: true,
      ...options,
    };
    uploadcare
      .openDialog(null, {
        publicKey: process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
        inputAcceptTypes: 'image/png, image/jpeg, application/pdf',
        ...opt,
      })
      .done((file) => {
        const arrayGroupFiles = file.files();
        let uploadedFiles = [];
        arrayGroupFiles.map((groupFile) => {
          return groupFile.done((x) => {
            const index = x.name.indexOf('.');
            if ((x?.size / 1024).toFixed(0) >= 10240) {
              if (typeof validate === 'function') validate();
              return;
            }
            uploadedFiles = [
              ...uploadedFiles,
              {
                file_name: x.name,
                url: x.cdnUrl,
                type: x.name.substr(index + 1),
                is_image: x.isImage,
              },
            ];
          });
        });
        resolve(uploadedFiles);
      })
      .fail(() => {
        resolve([]);
      });
  });

export const uploadFiles = (files, options, validate) =>
  new Promise((resolve) => {
    const opt = {
      publicKey: process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
      ...options,
    };
    Promise.all(
      files.map(
        (file) =>
          new Promise((r) => {
            if ((file?.size / 1024).toFixed(0) >= 10240) {
              if (typeof validate === 'function') validate();
              return;
            }
            uploadcare.fileFrom('object', file, opt).then(r);
          })
      )
    ).then((out) => {
      resolve(
        out.map((x) => {
          const index = x.name.indexOf('.');
          return {
            file_name: x.name,
            url: x.cdnUrl,
            type: x.name.substr(index + 1),
            is_image: x.isImage,
          };
        })
      );
    });
  });

const fileIcon = {
  pdf: IconFilePdf,
  jpeg: IconFileJpg,
  jpg: IconFileJpg,
  png: IconFilePng,
};

function FormFileDnd({
  id,
  label,
  description,
  required,
  value,
  name,
  onChange,
  onSetFieldValue,
  onSetFieldError,
  className,
  error,
  imagesOnly,
  max,
  rightAlign,
}) {
  const isError = !!error;
  const [isUploadBtnHovered, setIsUploadBtnHovered] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  const handleDrop = async (files, onIsUploading) => {
    if (!isEmpty(files)) {
      const limit = max - value.length;
      if (limit > 0) {
        onIsUploading(true);
        setIsUploading(true);
        const output = await uploadFiles(
          files.filter((_, i) => i < limit),
          {},
          () => {
            onIsUploading(false);
            setIsUploading(false);
            onSetFieldError(name, 'The uploaded file exceeds 10MB');
          }
        );
        onIsUploading(false);
        setIsUploading(false);
        if (typeof onChange === 'function' && files.length > 0) {
          onChange((state) => ({
            ...state,
            [name]: [...value, ...output] || [],
          }));
        }
        if (typeof onSetFieldValue === 'function' && files.length > 0) {
          onSetFieldValue(name, [...value, ...output] || []);
        }
      }
    }
  };
  const handleUpload = async () => {
    setIsUploading(true);
    const output = await uploadFile(
      {
        multipleMin: 1,
        multipleMax: max !== 0 ? max - value.length : 0,
        imagesOnly,
        inputAcceptTypes: imagesOnly
          ? 'image/png, image/jpeg'
          : 'image/png, image/jpeg, application/pdf',
      },
      () => {
        setIsUploading(false);
        onSetFieldError(name, 'The uploaded file exceeds 10MB');
      }
    );
    setIsUploading(false);
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: [...value, ...output],
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, [...value, ...output]);
    }
  };
  const handleRemove = (index) => {
    const items = value;
    if (typeof onChange === 'function') {
      items.splice(index, 1);
      onChange((state) => ({
        ...state,
        [name]: items,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      items.splice(index, 1);
      onSetFieldValue(name, items);
    }
  };

  const handleOpenPreview = (fileName, isImage, url) => {
    showLightbox({
      title: fileName,
      content: url,
      isImage,
    });
  };

  return (
    <>
      {!rightAlign && (
        <div className="flex flex-col">
          <label
            htmlFor={id}
            className="block text-xs font-medium mb-2 text-gray-500"
          >
            <span>{label}</span>
            {required && (
              <span className="ml-1 font-light text-red-500">*</span>
            )}
          </label>
          <DropZone
            onDrop={handleDrop}
            disabled={value.length === max}
            accept={
              imagesOnly
                ? 'image/png, image/jpeg'
                : 'image/png, image/jpeg, application/pdf'
            }
          >
            <div
              className={cn(
                'w-full rounded-lg bg-gray-100 relative z-[1]',
                value.length === max
                  ? ''
                  : 'hover:bg-primary-400 hover:border-primary-800 opacity-50',
                isError
                  ? 'border border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
                  : 'border-2 border-gray-300 border-dashed',
                className
              )}
              onFocus={() => {
                setIsUploadBtnHovered(false);
              }}
              onMouseOver={() => {
                if (value.length < max) setIsUploadBtnHovered(true);
              }}
              onMouseLeave={() => {
                setIsUploadBtnHovered(false);
              }}
            >
              <div className="h-full w-full px-3 pt-2 pb-3 flex flex-col">
                <button
                  type="button"
                  className={cn(
                    'flex-1 flex',
                    value.length === max ? 'cursor-default' : ''
                  )}
                  onClick={handleUpload}
                  disabled={isUploading || value.length === max}
                >
                  <div className="m-auto flex flex-col items-center justify-center">
                    {!isUploadBtnHovered && (
                      <>
                        <BiCloudUpload className="h-16 w-16 text-gray-300" />
                        <div className="m-auto text-gray-400 text-xs font-light mb-3 text-center">
                          {!isUploading ? (
                            <>
                              <p>Drag and Drop an image here</p>
                              <p>or click to upload</p>
                            </>
                          ) : (
                            <p>Uploading...</p>
                          )}
                        </div>
                      </>
                    )}

                    {isUploadBtnHovered && (
                      <>
                        <BiCloudUpload className="h-16 w-16 text-primary-800" />
                        <div className="m-auto text-gray-900 text-xs font-light mb-3 text-center">
                          Drop selected file here
                        </div>
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </DropZone>
          {!isEmpty(value) && (
            <div className="flex flex-col flex-1 space-y-2 pt-3">
              {value.map((item, i) => (
                <div
                  key={`${i + 1}-${item?.file_name}`}
                  className="flex flex-1 border rounded-lg py-2 px-3 items-center justify-between"
                >
                  <div className="flex space-x-2 items-center">
                    <img
                      src={fileIcon[item?.type]}
                      alt={`${item?.type}-icon`}
                    />
                    <button
                      className="text-sm"
                      type="button"
                      onClick={() =>
                        handleOpenPreview(
                          item?.file_name,
                          item?.is_image,
                          item?.url
                        )
                      }
                    >
                      {item?.file_name}
                    </button>
                  </div>
                  <button
                    className="text-red-500 hover:text-red-600 transition"
                    type="button"
                    onClick={() => handleRemove(i)}
                  >
                    <HiXCircle className="h-6 w-6 bg-red-50 p-1 mt-1 mr-1 rounded-full" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {rightAlign && (
        <div className="grid grid-cols-2 gap-5">
          <div className="flex flex-col">
            <label
              htmlFor={id}
              className="block text-xs font-medium mb-2 text-gray-500"
            >
              <span>{label}</span>
              {required && (
                <span className="ml-1 font-light text-red-500">*</span>
              )}
            </label>
            {description}
            {!isEmpty(value) && (
              <div className="flex flex-col space-y-2 pt-3">
                {value.map((item, i) => (
                  <div
                    key={`${i + 1}-${item?.file_name}`}
                    className="flex flex-1 border rounded-lg py-2 px-3 items-center justify-between"
                  >
                    <div className="flex space-x-2 items-center">
                      <img
                        src={fileIcon[item?.type]}
                        alt={`${item?.type}-icon`}
                      />
                      <button
                        className="text-sm"
                        type="button"
                        onClick={() =>
                          handleOpenPreview(
                            item?.file_name,
                            item?.is_image,
                            item?.url
                          )
                        }
                      >
                        {item?.file_name}
                      </button>
                    </div>
                    <button
                      className="text-red-500 hover:text-red-600 transition"
                      type="button"
                      onClick={() => handleRemove(i)}
                    >
                      <HiXCircle className="h-6 w-6 bg-red-50 p-1 mt-1 mr-1 rounded-full" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DropZone
            onDrop={handleDrop}
            disabled={value.length === max}
            accept={
              imagesOnly
                ? 'image/png, image/jpeg'
                : 'image/png, image/jpeg, application/pdf'
            }
          >
            <div
              className={cn(
                'w-full rounded-lg bg-gray-100 relative z-[1]',
                value.length === max
                  ? ''
                  : 'hover:bg-primary-400 hover:border-primary-800 opacity-50',
                isError
                  ? 'border border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
                  : 'border-2 border-gray-300 border-dashed',
                className
              )}
              onFocus={() => {
                setIsUploadBtnHovered(false);
              }}
              onMouseOver={() => {
                if (value.length < max) setIsUploadBtnHovered(true);
              }}
              onMouseLeave={() => {
                setIsUploadBtnHovered(false);
              }}
            >
              <div className="h-full w-full px-3 pt-2 pb-3 flex flex-col">
                <button
                  type="button"
                  className="flex-1 flex"
                  onClick={handleUpload}
                  disabled={isUploading || value.length === max}
                >
                  <div className="m-auto flex flex-col items-center justify-center">
                    {!isUploadBtnHovered && (
                      <>
                        <BiCloudUpload className="h-16 w-16 text-gray-300" />
                        <div className="m-auto text-gray-400 text-xs font-light mb-3 text-center">
                          {!isUploading ? (
                            <>
                              <p>Drag and Drop an image here</p>
                              <p>or click to upload</p>
                            </>
                          ) : (
                            <p>Uploading...</p>
                          )}
                        </div>
                      </>
                    )}

                    {isUploadBtnHovered && (
                      <>
                        <BiCloudUpload className="h-16 w-16 text-primary-800" />
                        <div className="m-auto text-gray-900 text-xs font-light mb-3 text-center">
                          Drop selected file here
                        </div>
                      </>
                    )}
                  </div>
                </button>
              </div>
            </div>
          </DropZone>
        </div>
      )}
    </>
  );
}

FormFileDnd.defaultProps = {
  id: '',
  value: [],
  label: '',
  description: null,
  required: false,
  error: null,
  className: 'aspect-h-2 aspect-w-3',
  onChange: false,
  onSetFieldValue: false,
  onSetFieldError: false,
  imagesOnly: false,
  rightAlign: false,
  max: 1,
};

FormFileDnd.propTypes = {
  id: PropTypes.string,
  value: PropTypes.instanceOf(Array),
  required: PropTypes.bool,
  imagesOnly: PropTypes.bool,
  rightAlign: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  description: PropTypes.instanceOf(Object),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldError: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  max: PropTypes.number,
};

export default FormFileDnd;
